import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import {
  Subscription,
  switchMap,
  combineLatest,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';
import { Store } from '@ngrx/store';

import {
  Professional,
  ProfessionalSearch,
  LastSearchPerformed,
} from '../../../models';
import {
  setProfessionalsSelectedBenefit,
  setProfessionalsResultsByBenefit,
  setSelectedProfessionalDetails,
  setProfessionalsLastSearchPerformed,
  setResetResultsByBenefit,
} from '../../../state/professionals/professionals.actions';
import {
  selectProfessionalsSelectedBenefit,
  selectProfessionalsResultsByBenefit,
  selectSelectedProfessionalDetails,
  selectProfessionalsSearchOptions,
  selectProfessionalsLastSearchPerformed,
} from '../../../state/professionals/professionals.selectors';
import { hideLoading, showLoading } from '../../../state/app/app.actions';
import { ProviderService } from '../../../services/provider.service';
import { StringUtils } from '../../../shared/utils/utils';

@Component({
  selector: 'app-professional-list',
  templateUrl: './professional-list.component.html',
  styleUrl: './professional-list.component.scss',
})
export class ProfessionalListComponent implements OnInit {
  private subscription = new Subscription();
  private userInteracted = false;
  @ViewChild('paginator') paginator: MatPaginator;
  matDataSource: MatTableDataSource<Professional> = new MatTableDataSource();
  professionalSearch: ProfessionalSearch = new ProfessionalSearch();
  professionalsSearchParam: ProfessionalSearch = new ProfessionalSearch();

  updateResultsByProvince$ = new BehaviorSubject<string>('all');
  updateResultsByProfession$ = new BehaviorSubject<string>('all');
  updateResultsBySpecialty$ = new BehaviorSubject<string>('all');
  paginatorState$ = new BehaviorSubject<any>({});

  loading: boolean = false;
  professionals: Professional[] = [];

  openDetailsByID?: number = null;
  selectedProfessional?: Professional = null;
  selectedBenefitTabIndex: number = 0;
  selectedBenefit?: string = null;
  selectedProvince?: string = null;
  selectedProfession?: string = null;
  selectedSpecialty?: string = null;
  selectedName?: string = null;

  addSearchFirstName = signal('');
  addSearchLastName = signal('');

  benefitSearchGroupsSelected: {
    province: string;
    professions: string[];
    specialties: string[];
  }[];
  filteredProfessions: string[] = [];
  filteredProvinces: string[] = [];
  filteredSpecialties: string[] = [];

  selectedRowIndex: number = 0;
  displayedColumns: string[] = ['results'];
  showConcernsOnly = false;

  resultsMessage: string = '';
  initalSearchMessage: string = 'Begin a search';
  noResultsMessage: string = 'No results found';
  noResultsWithConcerns: string = `The result doesn't contain items with concerns`;

  initialPageIndex: number = 0;
  initialPageSize: number = 20;
  initialTotalElements: number = 0;

  lastSearchPerformed?: LastSearchPerformed = null;
  hasValidLastSearchPerformed: boolean = false;
  isServerSearch: boolean = false;
  isSearchFromDisciplines: boolean = false;

  enableDeepSearch = signal(false);
  deepSearchStatus = signal<'idle' | 'loading' | 'success' | 'error'>('idle');
  defaultBenefits: string[] = ['Pharmacy'];

  constructor(
    private providerService: ProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store
  ) {
    this.professionalsSearchParam.benefits = this.defaultBenefits;
    this.resultsMessage = this.initalSearchMessage;
  }

  ngOnInit(): void {
    const getPreloadDataSub = combineLatest([
      this.store
        .select(selectProfessionalsSelectedBenefit)
        .pipe(distinctUntilChanged()),
      this.updateResultsByProvince$.pipe(distinctUntilChanged()),
      this.updateResultsByProfession$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        switchMap(([benefit, province, profession]) => {
          this.selectedBenefit = benefit;
          this.resultsMessage = this.initalSearchMessage;

          return this.store.select(
            selectProfessionalsResultsByBenefit(
              benefit,
              province || 'all',
              profession || 'all'
            )
          );
        }),
        distinctUntilChanged((prev, curr) => {
          const isSameContentThanPrevious =
            JSON.stringify(prev?.content) === JSON.stringify(curr?.content);
          const isViewSameLenghtAsResults =
            this.professionals.length === curr?.content?.length;

          return isSameContentThanPrevious && isViewSameLenghtAsResults;
        })
      )
      .subscribe((data) => {
        this.processResults(data);
      });

    const searchOptionsByBenefitSub = this.store
      .select(selectProfessionalsSearchOptions)
      .subscribe((searchOptions) => {
        if (searchOptions?.benefitSearchGroups?.length > 0) {
          this.professionalsSearchParam = {
            ...searchOptions,
            benefits: searchOptions?.benefitSearchGroups.map(
              (benefitGroup) => benefitGroup.benefit
            ),
          };

          // perform a benefit sync if not already selected
          if (!this.selectedBenefit) {
            this.selectedBenefit =
              searchOptions?.benefitSearchGroups[0].benefit;
            this.setRoute();
            this.store.dispatch(
              setProfessionalsSelectedBenefit({
                professionalsSelectedBenefit: this.selectedBenefit,
              })
            );
          }
          // ensure tab updates accordingly on reload or coming external navigation
          setTimeout(() => {
            this.selectedBenefitTabIndex =
              this.professionalsSearchParam.benefits.indexOf(
                this.selectedBenefit
              );
          });
          this.updateFiltersByBenefitSelected();
        }
      });

    const selectedProfessionalSub = this.store
      .select(selectSelectedProfessionalDetails)
      .pipe(distinctUntilChanged((prev, curr) => prev?.id === curr?.id))
      .subscribe((professional) => {
        this.selectedProfessional = professional;
        this.scrollToSelectedRow(professional);
      });

    const lastSearchPerformedSub = this.store
      .select(selectProfessionalsLastSearchPerformed)
      .pipe(distinctUntilChanged())
      .subscribe((lastSearchPerformed) => {
        this.lastSearchPerformed = lastSearchPerformed;
        this.hasValidLastSearchPerformed =
          !!this.lastSearchPerformed?.queryParams?.ben;
      });

    this.subscription.add(getPreloadDataSub);
    this.subscription.add(searchOptionsByBenefitSub);
    this.subscription.add(selectedProfessionalSub);
    this.subscription.add(lastSearchPerformedSub);
    this.subscription.add(this.updateResultsByProvince$);
    this.subscription.add(this.updateResultsByProfession$);
  }

  ngAfterViewInit() {
    this.paginatorState$.subscribe((data) => {
      if (this.paginator) {
        this.paginator.length = data.length;
        this.paginator.pageIndex = data.pageIndex;
      }
    });
    this.loadByQueryParamsOrLastPerformed();
  }

  private processResults(data: any): void {
    this.professionals = [];
    this.matDataSource.data = [];
    this.paginatorState$.next({
      length: 0,
      pageIndex: 0,
    });

    if (this.hasMinimumSearchQueryParams) {
      if (data?.content?.length == 0 && this.isServerSearch) {
        this.resultsMessage = this.noResultsMessage;
        if (!!this.selectedProfessional?.id) {
          this.onCloseDetails();
        }
      } else {
        this.setRoute();
      }
      this.professionals = data.content || [];
      this.matDataSource.data = data.content;
      this.showConcernsOnly = this.isSearchFromOtherPage
        ? false
        : data.concerns;
      this.paginatorState$.next({
        length: data.length,
        pageIndex: data.pageIndex,
      });
      this.autoSelectItemDetails();
    }
  }

  private loadByQueryParamsOrLastPerformed() {
    this.isSearchFromDisciplines = history.state.searchFromDisciplines;
    this.openDetailsByID = Number(this.route.snapshot.queryParamMap.get('id'));
    const benefit =
      this.route.snapshot.queryParamMap.get('ben') ||
      this.lastSearchPerformed?.queryParams?.ben;
    this.selectedProvince =
      this.route.snapshot.queryParamMap.get('prov') ||
      this.lastSearchPerformed?.queryParams?.prov;
    this.selectedProfession = this.isSearchFromOtherPage
      ? null
      : this.route.snapshot.queryParamMap.get('prof') ||
        this.lastSearchPerformed?.queryParams?.prof;
    this.selectedSpecialty = this.isSearchFromOtherPage
      ? null
      : this.route.snapshot.queryParamMap.get('spec') ||
        this.lastSearchPerformed?.queryParams?.spec;        
    this.selectedName =
      this.route.snapshot.queryParamMap.get('n') ||
      this.lastSearchPerformed?.queryParams?.n;

    if (!this.selectedBenefit || (benefit && benefit != this.selectedBenefit)) {
      const professionalsSelectedBenefit = benefit
        ? benefit
        : this.professionalsSearchParam.benefits[0];

      this.store.dispatch(
        setProfessionalsSelectedBenefit({
          professionalsSelectedBenefit,
        })
      );
    }

    const isDataInQueryParams =
      !!this.route.snapshot.queryParamMap.get('ben') ||
      !!this.route.snapshot.queryParamMap.get('prov') ||
      !!this.route.snapshot.queryParamMap.get('prof') ||
      !!this.route.snapshot.queryParamMap.get('n');

    if (
      isDataInQueryParams ||
      this.openDetailsByID ||
      this.isSearchFromOtherPage
    ) {
      this.searchPaginatedProfessionals(true);
    } else {
      this.setRoute();
      if (this.selectedProvince) {
        this.onProvinceSelected(this.selectedProvince);
      }
      if (this.selectedProfession) {
        this.onProfessionSelected(this.selectedProfession);
      }
      if (this.selectedSpecialty) {
        this.onSpecialtySelected(this.selectedSpecialty);
      }
    }
  }

  setSearchParams(): void {
    this.professionalSearch.id = null;
    if (this.openDetailsByID) {
      this.professionalSearch.id = this.openDetailsByID;
    }

    if (!this.selectedBenefit) {
      this.selectedBenefit =
        this.professionalsSearchParam.benefits[this.selectedBenefitTabIndex];
    }

    this.professionalSearch.benefits = [this.selectedBenefit];
    this.professionalSearch.provinces = [this.selectedProvince];
    this.professionalSearch.professions = [this.selectedProfession];
    this.professionalSearch.specialties = [this.selectedSpecialty];
    this.professionalSearch.name = this.selectedName
      ?.trim()
      ?.replace(/\s+/g, ' ');
    this.professionalSearch.concerns = this.showConcernsOnly ? 'Y' : 'N';
    this.professionalSearch.pageSize =
      this.paginator?.pageSize || this.initialPageSize;
    this.professionalSearch.pageNumber =
      this.paginator?.pageIndex || this.initialPageIndex;
  }

  searchPaginatedProfessionals(
    isInitialSearch: boolean = false,
    triggerFromUI: boolean = false
  ): void {
    if (this.hasMinimumSearchQueryParams) {
      if (isInitialSearch) {
        this.loading = true;
        this.store.dispatch(showLoading());
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
      }

      if (triggerFromUI) {
        this.resetControlFlags();
      }

      let cacheConcernsAtSetParams = this.showConcernsOnly;
      this.setSearchParams();

      this.providerService
        .searchPaginatedProfessionals(this.professionalSearch)
        .subscribe((providersResults: ProfessionalSearch) => {
          const content = providersResults?.pageResult?.content;
          this.isServerSearch = true;

          this.store.dispatch(
            setProfessionalsResultsByBenefit({
              benefit: this.selectedBenefit,
              province: this.selectedProvince || 'all',
              profession: this.selectedProfession || 'all',
              specialty: this.selectedSpecialty || 'all',
              professionalsResultsData: content,
              length: providersResults?.pageResult?.totalElements,
              pageIndex: this.paginator.pageIndex,
              concerns: cacheConcernsAtSetParams,
            })
          );

          this.updateResultsByProvince$.next(this.selectedProvince); // load UI via subsciption
          this.updateResultsByProfession$.next(this.selectedProfession); // load UI via subsciption
          this.updateResultsBySpecialty$.next(this.selectedSpecialty); // load UI via subsciption
          this.setRoute();

          if (isInitialSearch) {
            this.store.dispatch(hideLoading());
            this.loading = false;
            this.resultsMessage = this.noResultsMessage;
          }
        });
    }
  }
  private autoSelectItemDetails() {
    const initialSelectedItem = this.selectedProfessional;

    let newSelectedItem = null;
    if (this.openDetailsByID && this.professionals.length > 0) {
      newSelectedItem =
        this.professionals.find((p) => p.id === this.openDetailsByID) ??
        this.professionals[0];
    } else if (this.isSearchFromDisciplines && this.professionals.length > 0) {
      newSelectedItem = this.professionals[0];
    } else if (
      initialSelectedItem ||
      !!this.lastSearchPerformed?.selectedItem?.id
    ) {
      newSelectedItem = !!this.lastSearchPerformed?.selectedItem?.id
        ? this.lastSearchPerformed.selectedItem
        : this.professionals[0];
    } else {
      newSelectedItem = null;
    }

    if (initialSelectedItem?.id != newSelectedItem?.id) {
      this.dispatchSelectedItem(newSelectedItem);
    }
  }

  clearName() {
    this.selectedName = '';
    if (this.openDetailsByID) {
      this.openDetailsByID = null;
    }
  }

  clear() {
    this.professionalSearch = new ProfessionalSearch();
    this.openDetailsByID = null;
    this.selectedBenefit = null;
    this.selectedName = null;
    this.selectedProvince = null;
    this.selectedProfession = null;
    this.selectedSpecialty = null;
    this.resultsMessage = this.initalSearchMessage;
    this.professionals = [];
    this.paginator.pageIndex = this.initialPageIndex;
    this.paginator.pageSize = this.initialPageSize;
    this.paginator.length = this.initialTotalElements;
    this.matDataSource.data = [];
    this.showConcernsOnly = false;
    this.resetControlFlags();
    this.setRoute();
    this.resetResultsState();
    this.addSearchFirstName.set('');
    this.addSearchLastName.set('');
  }

  private resetResultsState() {
    this.store.dispatch(setResetResultsByBenefit());
  }

  private setRoute() {
    const queryParams = {
      ben: this.selectedBenefit,
      prov: this.selectedProvince,
      prof: this.selectedProfession,
      spec: this.selectedSpecialty,
      n: this.selectedName || undefined,
      id: this.openDetailsByID || undefined,
    };

    this.store.dispatch(
      setProfessionalsLastSearchPerformed({
        lastSearchPerformed: {
          queryParams,
        },
      })
    );

    this.router.navigate([], {
      queryParams,
    });
  }


  onBenefitTabIndexChange(index: number): void {
    const newBenefit = this.professionalsSearchParam.benefits[index];
    if (this.selectedBenefit !== newBenefit) {
      this.resetControlFlags();
      this.store.dispatch(
        setProfessionalsSelectedBenefit({
          professionalsSelectedBenefit: newBenefit,
        })
      );
      this.updateFiltersByBenefitSelected();
    }
  }

  onUserInteractChange(): void {
    this.userInteracted = true;
  }

  onProvinceSelected(value: string): void {
    if (this.userInteracted) {
      this.resetControlFlags();
      this.selectedProfession = null;
      this.selectedSpecialty = null;
      this.updateResultsByProfession$.next(null);
    }

    this.filteredProfessions =
      this.benefitSearchGroupsSelected?.find((prov) => prov.province === value)
        ?.professions || [];

    this.filteredSpecialties =
      this.benefitSearchGroupsSelected?.find((prov) => prov.province === value)
        ?.specialties || [];

    this.updateResultsByProvince$.next(value);
  }

  private resetControlFlags() {
    this.userInteracted = false;
    this.isServerSearch = false;
    this.isSearchFromDisciplines = false;
    this.onCloseDetails();
  }

  onProfessionSelected(value: string): void {
    if (this.userInteracted) {
      this.resetControlFlags();
    }
    this.updateResultsByProfession$.next(value);
  }
  onSpecialtySelected(value: string): void {
    if (this.userInteracted) {
      this.resetControlFlags();
    }
    this.updateResultsBySpecialty$.next(value);
  }

  openDetails(professional: Professional, index?: number): void {
    if (index || index == 0) {
      this.selectedRowIndex = this.getActualIndex(index);
    }

    this.scrollToSelectedRow(professional);
    this.dispatchSelectedItem(professional);
  }

  private dispatchSelectedItem(professional) {
    this.store.dispatch(
      setSelectedProfessionalDetails({
        selectedProfessionalDetails: professional,
      })
    );
    this.store.dispatch(
      setProfessionalsLastSearchPerformed({
        lastSearchPerformed: {
          selectedItem: professional,
        },
      })
    );
  }

  scrollToSelectedRow(selectedItem): void {
    if (selectedItem) {
      setTimeout(() => {
        const rowElement = document.getElementById(`row-${selectedItem?.id}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  onCloseDetails(): void {
    this.store.dispatch(
      setSelectedProfessionalDetails({
        selectedProfessionalDetails: null,
      })
    );

    this.store.dispatch(
      setProfessionalsLastSearchPerformed({
        lastSearchPerformed: {
          selectedItem: null,
        },
      })
    );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.matDataSource.filter = filterValue.trim().toLowerCase();

    if (this.matDataSource.paginator) {
      this.matDataSource.paginator.firstPage();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.matDataSource.data.length === 0) return;

    const getMaxIndexOnCurrentPage = (): number => {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
      const totalRecords = this.paginator.length;
      const endIndex = Math.min(
        startIndex + this.paginator.pageSize - 1,
        totalRecords - 1
      );
      return endIndex;
    };

    const performUpdateInStore = (selectedRowIndex: number) => {
      const relativeIndex = selectedRowIndex % this.paginator.pageSize;
      const newSelectedProvider = this.matDataSource.data[relativeIndex];
      this.dispatchSelectedItem(newSelectedProvider);
    };

    const currentStartIndex =
      this.paginator.pageIndex * this.paginator.pageSize;
    const maxIndex = getMaxIndexOnCurrentPage();

    if (event.key === 'ArrowDown') {
      if (this.selectedRowIndex < maxIndex) {
        this.selectedRowIndex++;
        performUpdateInStore(this.selectedRowIndex);
      }
    } else if (event.key === 'ArrowUp') {
      if (this.selectedRowIndex > currentStartIndex) {
        this.selectedRowIndex--;
        performUpdateInStore(this.selectedRowIndex);
      }
    }
  }

  private getActualIndex(index: number): number {
    return this.paginator.pageIndex * this.paginator.pageSize + index;
  }

  private updateFiltersByBenefitSelected(): void {
    const benefitSearchGroupsSelected =
      this.professionalsSearchParam.benefitSearchGroups?.filter(
        (benefitGroup) => benefitGroup.benefit === this.selectedBenefit
      )[0]?.provinces || [];
    this.filteredProvinces =
      benefitSearchGroupsSelected?.map((prov) => prov?.province) || [];
    this.benefitSearchGroupsSelected = benefitSearchGroupsSelected;

    if (this.selectedProvince) {
      this.onProvinceSelected(this.selectedProvince);
    }
  }

  // special to professionals page
  formatUrl(url: string): string {
    return StringUtils.formatUrl(url);
  }

  // special to professionals page
  formatAuthorizations(string: string): string[] {
    return StringUtils.parseSemiColonSegments(string);
  }

  // special to professionals page
  trimEffectiveDate(string: string): string {
    return StringUtils.trimEffectiveDate(string);
  }

  get hasMinimumSearchQueryParams(): boolean {
    return !!this.selectedProvince;
  }

  get isSearchFromOtherPage(): boolean {
    return this.isSearchFromDisciplines || !!this.openDetailsByID;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.store.dispatch(
      setSelectedProfessionalDetails({
        selectedProfessionalDetails: null,
      })
    );
  }

  get canSubmitDeepSearch(): boolean {
    return (
      this.addSearchFirstName().trim() !== '' &&
      this.addSearchLastName().trim() !== '' &&
      !!this.selectedBenefit &&
      !!this.selectedProvince &&
      !!this.selectedProfession
    );
  }

  get showAddSearchButton(): boolean {
    return (
      this.resultsMessage === this.noResultsMessage &&
      this.matDataSource.data.length == 0 &&
      this.deepSearchStatus() !== 'success' &&
      !this.loading
    );
  }

  enableDeepSearchForm(): void {
    let firstName = '';
    let lastName = '';
    if (this.selectedName) {
      const [name, ...rest] = this.selectedName?.split(' ');
      firstName = name;
      lastName = rest.length > 0 ? rest.join(' ') : '';
    }
    this.deepSearchStatus.set('idle');
    this.enableDeepSearch.set(true);
    this.addSearchFirstName.set(firstName?.trim());
    this.addSearchLastName.set(lastName?.trim());
  }

  disableDeepSearch() {
    this.enableDeepSearch.set(false);
    this.addSearchFirstName.set('');
    this.addSearchLastName.set('');
    this.deepSearchStatus.set('idle');
  }

  handleRequestSearch(): void {
    const fullName =
      `${this.addSearchFirstName()} ${this.addSearchLastName()}`.trim();

    if (!fullName || !this.selectedProvince || !this.selectedProfession || !this.selectedSpecialty) {
      this.resultsMessage = 'Please fill in the required fields to search.';
      return;
    }

    this.selectedName = fullName;
    this.setSearchParams();
    this.professionalSearch.name = fullName;

    this.loading = true;
    this.store.dispatch(showLoading());
    this.deepSearchStatus.set('loading');
    this.providerService
      .searchPaginatedProfessionals(this.professionalSearch)
      .subscribe({
        next: (result) => {
          if (result.pageResult.content.length > 0) {
            this.handleSuccessfulSearch(result);
          } else {
            this.callAddSearch();
          }
        },
        error: (_) => {
          this.callAddSearch();
        },
      });
  }

  private callAddSearch(): void {
    const payload = {
      benefit: this.selectedBenefit,
      province: this.selectedProvince,
      profession: this.selectedProfession,
      specialty: this.selectedSpecialty,
      firstName: this.addSearchFirstName(),
      lastName: this.addSearchLastName(),
    };

    this.providerService.addSearch(payload).subscribe({
      next: () => {
        this.deepSearchStatus.set('success');
      },
      error: (_) => {
        this.deepSearchStatus.set('error');
      },
      complete: () => {
        this.store.dispatch(hideLoading());
        this.loading = false;
      },
    });
  }

  private handleSuccessfulSearch(result: ProfessionalSearch): void {
    this.store.dispatch(
      setProfessionalsResultsByBenefit({
        benefit: this.selectedBenefit,
        province: this.selectedProvince || 'all',
        profession: this.selectedProfession || 'all',
        specialty: this.selectedSpecialty || 'all',
        professionalsResultsData: result.pageResult.content,
        length: result.pageResult.totalElements,
        pageIndex: this.paginator.pageIndex,
        concerns: false,
      })
    );

    this.store.dispatch(hideLoading());
    this.loading = false;

    if (this.enableDeepSearch()) {
      this.disableDeepSearch();
    }
  }

  export() {
    const totalResults = this.paginator?.length;
    this.setSearchParams();
    this.loading = true;
    this.store.dispatch(showLoading());
    this.providerService
      .exportProfessionals({
        ...this.professionalSearch,
        professions: this.selectedProfession ? [this.selectedProfession] : null,
        specialties: this.selectedSpecialty ? [this.selectedSpecialty] : null,
        pageNumber: 0,
        pageSize: totalResults,
      })
      .subscribe(() => {
        this.loading = false;
        this.store.dispatch(hideLoading());
      });
  }
}
